import { useState, useEffect, type ReactNode } from 'react';
import { FileText } from 'lucide-react';
import { supabase } from '@core/supabase/client';
import { Modal } from '@shared/components/ui/Modal';
import type { OrgUser } from '@core/types';
import type { ListRightsUserRow } from '@shared/lib/listManageRightsPolicy';
import {
  defaultGenericListRightsPolicy,
  loadGenericListRightsPolicy,
  normalizeListRightsUserRow,
  saveGenericListRightsPolicy,
  type GenericListRightsPolicy,
} from '@shared/lib/listManageRightsPolicy';

type Props = {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  /** Stable key for localStorage, e.g. `clients`, `jobs`. */
  entityKey: string;
  title?: string;
  subtitle?: string;
  /** Optional body above the user table (e.g. future column toggles). */
  children?: ReactNode;
};

export function GenericManageRightsModal({
  open,
  onClose,
  orgId,
  entityKey,
  title = 'Manage Rights',
  subtitle = 'Per-user access for this list — Read, write, delete, and child-object operations.',
  children,
}: Props) {
  const [policy, setPolicy] = useState<GenericListRightsPolicy>(defaultGenericListRightsPolicy);
  const [users, setUsers] = useState<OrgUser[]>([]);

  useEffect(() => {
    if (!open || !orgId) return;
    setPolicy(loadGenericListRightsPolicy(orgId, entityKey));
  }, [open, orgId, entityKey]);

  useEffect(() => {
    if (!open || !orgId) return;
    let cancelled = false;
    void supabase
      .from('ggj_b2b_org_users')
      .select('id, org_id, keycloak_sub, display_name, email, role, is_active, invited_at, joined_at')
      .eq('org_id', orgId)
      .eq('is_active', true)
      .order('display_name')
      .then(({ data }) => {
        if (!cancelled && data) setUsers(data as OrgUser[]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, orgId]);

  if (!orgId) return null;

  const patchUser = (userId: string, field: keyof ListRightsUserRow, value: boolean) => {
    setPolicy((prev) => {
      const cur = normalizeListRightsUserRow(prev.userAccess[userId]);
      return {
        ...prev,
        userAccess: {
          ...prev.userAccess,
          [userId]: { ...cur, [field]: value },
        },
      };
    });
  };

  const handleSave = () => {
    saveGenericListRightsPolicy(orgId, entityKey, policy);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={title} subtitle={subtitle} icon={<FileText className="w-4 h-4" />} size="xl">
      <div className="space-y-4 text-sm max-h-[min(70vh,520px)] overflow-y-auto pr-1">
        {children}

        <div>
          <p className="font-semibold vscode-text-primary mb-2">Per-user access</p>
          <p className="text-[11px] vscode-text-tertiary mb-2">
            Active org members (name and email). These flags are stored for this list and are intended to align with future
            enforcement; server RBAC and roles still apply for destructive actions.
          </p>
          <div className="border border-[var(--vscode-border-primary)] rounded-lg overflow-x-auto">
            <table className="w-full text-xs min-w-[640px]">
              <thead>
                <tr className="vscode-bg-tertiary border-b border-[var(--vscode-border-primary)]">
                  <th className="text-left px-2 py-2">User</th>
                  <th className="text-center px-1 py-2 border-l border-[var(--vscode-border-primary)]" title="View this list and open records">
                    Read
                  </th>
                  <th className="text-center px-1 py-2" title="Create or edit from this context">
                    Write
                  </th>
                  <th className="text-center px-1 py-2" title="Delete rows">
                    Delete
                  </th>
                  <th className="text-center px-1 py-2" title="Dependent records (e.g. applications, children)">
                    Operate children
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-4 text-center vscode-text-tertiary">
                      No active org users found.
                    </td>
                  </tr>
                ) : (
                  users.map((u) => {
                    const a = normalizeListRightsUserRow(policy.userAccess[u.id]);
                    return (
                      <tr key={u.id} className="border-b border-[var(--vscode-border-primary)] last:border-0">
                        <td className="px-2 py-1.5 align-top">
                          <span className="vscode-text-primary font-medium">{u.display_name}</span>
                          <span className="block vscode-text-tertiary truncate max-w-[220px]">{u.email}</span>
                        </td>
                        {(['canRead', 'canWrite', 'canDelete', 'canOperateChildren'] as const).map((field, i) => (
                          <td
                            key={field}
                            className={`text-center px-1 py-1 ${i === 0 ? 'border-l border-[var(--vscode-border-primary)]' : ''}`}
                          >
                            <input
                              type="checkbox"
                              className="rounded"
                              checked={a[field]}
                              onChange={(e) => patchUser(u.id, field, e.target.checked)}
                              aria-label={`${u.display_name} — ${field}`}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] vscode-text-tertiary mt-1">
            Read defaults on for new rows. Team roles under Settings → Team remain the source of truth for production access.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-[var(--vscode-border-primary)]">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
}
